import { AppSettings } from '@/settings/settings'
import type { Component } from 'vue'
import type { Router } from 'vue-router'
import type { INavigationHelper } from './INavigationHelper'
import { NavigationHelper as DefaultNavigationHelper } from './default/navigationhelper'
import { NavigationHelper as ThreeCapNavigationHelper } from './3cap/navigationhelper'

// Define theme types
type ThemeKey = 'default' | 'compact' | '3cap'
type ColorSchemeKey = 'default' | 'ocean' | 'royal-blue'
type ThemeModeKey = "light" | "dark"

// Singleton navigation helper instance
let _navigationHelper: INavigationHelper | null = null

// Function to dynamically get the layout component based on theme setting
export function getThemeLayout(): Component {
  const themeName = AppSettings.theme() as ThemeKey

  // Dynamically import the layout component based on theme name
  switch (themeName) {
    case '3cap':
      return () => import('./3cap/ThreeCapLayout.vue')
    case 'default':
    default:
      return () => import('./default/DefaultLayout.vue')
  }
}

// Alternative: Get theme layout component synchronously (if you need it)
export async function getThemeLayoutSync(): Promise<Component> {
  const themeName = AppSettings.theme() as ThemeKey

  switch (themeName) {
    case '3cap':
      return (await import('./3cap/ThreeCapLayout.vue')).default
    case 'default':
    default:
      return (await import('./default/DefaultLayout.vue')).default
  }
}

// Function to dynamically load color scheme CSS
export async function loadColorScheme(colorScheme:string): Promise<void> {
  colorScheme = colorScheme ?? AppSettings.colorScheme()
  try {
    // Use dynamic import which Vite processes correctly
    switch (colorScheme) {
      case 'ocean':
        await import('./shared/color-schemes/ocean.css')
        break
      case 'royal-blue':
        await import('./shared/color-schemes/royal-blue.css')
        break
      case 'default':
      default:
        await import('./shared/color-schemes/default.css')
        break
    }
    
    // Apply the color scheme to the root element
    document.documentElement.setAttribute('data-color-scheme', colorScheme)
    
    
    console.log(`Color scheme '${colorScheme}' loaded successfully`)
  } catch (error) {
    console.error('Failed to load color scheme:', error)
    // Fallback to default if loading fails
    document.documentElement.setAttribute('data-color-scheme', 'default')
  }
}

// Function to apply color scheme (can be called to change color scheme at runtime)
export function applyColorScheme(colorScheme: ColorSchemeKey): void {
  document.documentElement.setAttribute('data-color-scheme', colorScheme)
}

// Get current color scheme
export function getCurrentColorScheme(): ColorSchemeKey {
  return AppSettings.colorScheme() as ColorSchemeKey
}

/**
 * Get the navigation helper for the current theme.
 * Creates the instance on first call based on the active theme.
 */
export function getNavigationHelper(): INavigationHelper {
  if (!_navigationHelper) {
    const themeName = AppSettings.theme() as ThemeKey

    switch (themeName) {
      case '3cap':
        _navigationHelper = ThreeCapNavigationHelper.getInstance()
        break
      case 'default':
      default:
        _navigationHelper = DefaultNavigationHelper.getInstance()
        break
      // case 'compact':
      //   _navigationHelper = CompactNavigationHelper.getInstance()
      //   break
    }
  }
  return _navigationHelper!
}

/**
 * Initialize the navigation helper with the Vue Router instance.
 * Call this once after the router is created.
 */
export function initNavigationHelper(router: Router): INavigationHelper {
  const helper = getNavigationHelper()
  helper.init(router)
  return helper
}
